import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Download } from 'lucide-react'
import { hrApi } from '@/services/api/hr'
import { apiClient } from '@/services/api/client'
import type {
  HrSaldoRow, HrStatementResponse, HrPeriodInfo, HrPeriodCloseResult, HrIpbImportPreview,
} from '@/types/hr'
import { SALARY_EXPORT_FIELDS } from '@/types/hr'
import { extractErrorMessage } from '@/utils/errorHandler'
import { TX_TYPE_LABELS, periodTypeLabel, formatHrDate } from '@/utils/hrLabels'
import { HrPageHeader } from '@/components/hr/HrPageHeader'
import { HrModuleInactiveNotice } from '@/components/hr/HrModuleInactiveNotice'

const ALL_TABS = [
  { key: 'salden', label: 'IPB-Salden' },
  { key: 'import', label: 'Import' },
  { key: 'statement', label: 'Kontoauszug' },
  { key: 'manual', label: 'Manuelle Buchung' },
  { key: 'periods', label: 'Perioden' },
  { key: 'export', label: 'Lohn-Export' },
] as const

type TabKey = typeof ALL_TABS[number]['key']

/** HR-v3 (R8): Funktions-Tabs nur bei aktivem func.*-Scope */
const TAB_SCOPES: Partial<Record<TabKey, string>> = {
  import: 'func.manual_adjustments',
  manual: 'func.manual_adjustments',
  periods: 'func.period_close',
  export: 'func.salary_export',
}

function fmt(n: number): string {
  return n.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function HRIpbPage() {
  const [params, setParams] = useSearchParams()
  const tab = (params.get('tab') as TabKey) || 'salden'
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [moduleInactive, setModuleInactive] = useState(false)
  const [funcScopes, setFuncScopes] = useState<Record<string, boolean>>({})

  useEffect(() => {
    hrApi.getMyScopes()
      .then(s => {
        setModuleInactive(!s.pages.ipb_payouts)
        const active: Record<string, boolean> = {}
        for (const sc of s.scopes) active[sc.key] = sc.module_active
        setFuncScopes(active)
      })
      .catch(() => { /* Fallback: Tabs sichtbar, API regelt Rest */ })
  }, [])

  const tabs = ALL_TABS.filter(t => {
    const scope = TAB_SCOPES[t.key]
    return !scope || funcScopes[scope] !== false
  })
  const visibleTab = tabs.some(t => t.key === tab) ? tab : 'salden'

  const setTab = (t: TabKey) => {
    setParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('tab', t)
      return next
    })
  }

  if (moduleInactive) {
    return <HrModuleInactiveNotice title="IPB & Auszahlungen" />
  }

  return (
    <div className="flex flex-col min-h-full">
      <HrPageHeader
        title="IPB & Auszahlungen"
        description="Lektionenkonto in Lektionen. Der offizielle Stand kommt aus dem externen System — hier anzeigen, setzen oder per CSV/Excel abgleichen."
      />

      <div className="flex-1 min-h-0 overflow-y-auto p-6">
        {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>}
        {notice && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">{notice}</div>}

        {/* hr2-v2 (E9): Standard-Tabs statt Eigenbau-border-2-Stil */}
        <div className="flex gap-1 border-b border-gray-200 mb-4 flex-wrap">
          {tabs.map(t => (
            <button key={t.key} type="button" onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 text-sm font-medium transition border-b-2 -mb-px ${
                visibleTab === t.key
                  ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {visibleTab === 'salden' && <SaldenTab onError={setError} onNotice={setNotice} />}
        {visibleTab === 'import' && <ImportTab onError={setError} onNotice={setNotice} />}
        {visibleTab === 'statement' && <StatementTab onError={setError} />}
        {visibleTab === 'manual' && <ManualTab onError={setError} onNotice={setNotice} />}
        {visibleTab === 'periods' && <PeriodsTab onError={setError} onNotice={setNotice} />}
        {visibleTab === 'export' && <ExportTab onError={setError} onNotice={setNotice} />}
      </div>
    </div>
  )
}

// ===========================================
// Salden
// ===========================================

function SaldenTab({ onError, onNotice }: { onError: (s: string) => void; onNotice: (s: string) => void }) {
  const [rows, setRows] = useState<HrSaldoRow[]>([])
  const [loading, setLoading] = useState(true)
  const [targets, setTargets] = useState<Record<string, string>>({})
  const [busyId, setBusyId] = useState<string | null>(null)
  const [q, setQ] = useState('')

  const load = useCallback(() => {
    return hrApi.getSalden({ include_zero: true })
      .then(r => setRows(r.salden))
      .catch(e => onError(extractErrorMessage(e, { loadError: 'Salden konnten nicht geladen werden' })))
  }, [onError])

  useEffect(() => {
    load().finally(() => setLoading(false))
  }, [load])

  const setSaldo = async (row: HrSaldoRow) => {
    const raw = (targets[row.teacher_id] ?? '').replace(',', '.')
    const target = parseFloat(raw)
    if (Number.isNaN(target)) { onError('Ungültiger Saldo'); return }
    if (!window.confirm(`${row.teacher_name}: Stand ${fmt(row.saldo)} → ${fmt(target)} setzen?`)) return
    setBusyId(row.teacher_id); onError('')
    try {
      const r = await hrApi.setTeacherSaldo(row.teacher_id, {
        target,
        reason: `Saldo-Anpassung ${fmt(row.saldo)} → ${fmt(target)}`,
      })
      onNotice(r.unchanged ? `${row.teacher_name}: unverändert.` : `${row.teacher_name}: Stand auf ${fmt(target)} gesetzt.`)
      setTargets(t => { const n = { ...t }; delete n[row.teacher_id]; return n })
      await load()
    } catch (e: unknown) {
      onError(extractErrorMessage(e, { loadError: 'Saldo konnte nicht gesetzt werden' }))
    } finally { setBusyId(null) }
  }

  if (loading) return <div className="text-gray-500 text-sm">Laden...</div>
  const qn = q.trim().toLowerCase()
  const visible = qn
    ? rows.filter(r => `${r.teacher_name} ${r.teacher_code ?? ''}`.toLowerCase().includes(qn))
    : rows
  return (
    <div>
      <p className="text-xs text-gray-500 mb-3">
        Interner Stand. «Neuer Stand» erzeugt eine Ausgleichsbuchung auf den Wert aus dem externen System.
      </p>
      <input value={q} onChange={e => setQ(e.target.value)}
        className="mb-3 w-full max-w-sm text-sm border rounded-md px-2.5 py-1.5"
        placeholder="Lehrer suchen…" />
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Lehrer</th>
              <th className="text-left px-4 py-2 font-medium">Ist-Stand</th>
              <th className="text-left px-4 py-2 font-medium">Neuer Stand</th>
              <th className="text-left px-4 py-2 font-medium">Periode</th>
              <th className="text-left px-4 py-2 font-medium">Auszahlung</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visible.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-4 text-center text-gray-500">Keine Lehrer</td></tr>
            )}
            {visible.map(r => (
              <tr key={r.teacher_id} className={r.negative_warning ? 'bg-red-50/50' : ''}>
                <td className="px-4 py-3 font-medium">
                  {r.teacher_name}{r.teacher_code ? <span className="text-gray-400 text-xs ml-1.5">({r.teacher_code})</span> : null}
                  {r.negative_warning && <span className="ml-2 text-xs text-red-600">Negativ-Warnung</span>}
                </td>
                <td className={`px-4 py-3 font-bold ${r.saldo > 0 ? 'text-green-600' : r.saldo < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                  {r.saldo > 0 ? '+' : ''}{fmt(r.saldo)}
                </td>
                <td className="px-4 py-3">
                  <input className="w-24 text-sm border border-gray-300 rounded-md px-2 py-1"
                    placeholder={fmt(r.saldo)}
                    value={targets[r.teacher_id] ?? ''}
                    onChange={e => setTargets(t => ({ ...t, [r.teacher_id]: e.target.value }))} />
                </td>
                <td className="px-4 py-3 text-gray-500">{r.period_delta > 0 ? '+' : ''}{fmt(r.period_delta)}</td>
                <td className="px-4 py-3 text-gray-500">{fmt(r.payout_lessons)}</td>
                <td className="px-4 py-3 text-right">
                  <button type="button"
                    disabled={busyId === r.teacher_id || !(targets[r.teacher_id] ?? '').trim()}
                    onClick={() => void setSaldo(r)}
                    className="text-xs bg-indigo-600 text-white rounded-md px-2.5 py-1 disabled:opacity-50">
                    Setzen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ImportTab({ onError, onNotice }: { onError: (s: string) => void; onNotice: (s: string) => void }) {
  const [preview, setPreview] = useState<HrIpbImportPreview | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)

  const onFile = async (file: File | undefined) => {
    if (!file) return
    setBusy(true); onError(''); setPreview(null)
    try {
      const p = await hrApi.previewIpbImport(file)
      setPreview(p)
      setSelected(new Set(p.matched.filter(r => r.delta !== 0).map(r => r.teacher_id)))
    } catch (e: unknown) {
      onError(extractErrorMessage(e, { loadError: 'Datei konnte nicht gelesen werden' }))
    } finally { setBusy(false) }
  }

  const apply = async () => {
    if (!preview) return
    const items = preview.matched.filter(r => selected.has(r.teacher_id)).map(r => ({
      teacher_id: r.teacher_id, import_saldo: r.import_saldo, current_saldo: r.current_saldo,
    }))
    if (!items.length) { onError('Keine Zeilen ausgewählt'); return }
    if (!window.confirm(`${items.length} Stand/Stände auf den Importwert setzen?`)) return
    setBusy(true); onError('')
    try {
      const r = await hrApi.applyIpbImport(items)
      onNotice(`${r.applied.length} Stand/Stände gesetzt.` + (r.skipped.length ? ` ${r.skipped.length} übersprungen.` : ''))
      setPreview(null)
      setSelected(new Set())
    } catch (e: unknown) {
      onError(extractErrorMessage(e, { loadError: 'Import fehlgeschlagen' }))
    } finally { setBusy(false) }
  }

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded-lg p-3">
        Das offizielle IPB wird extern geführt. Die Datei setzt den <b>internen Stand</b> auf den importierten Wert
        (Ausgleichsbuchung, Ledger bleibt unveränderlich). Spalten: <code>teacher_code</code>, <code>saldo</code>, optional <code>email</code>.
        Vorlage ist CSV (Semikolon). Excel-Export als .xlsx ist ebenfalls möglich.
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className="text-xs border rounded-md px-2.5 py-1.5 bg-white"
          onClick={() => { void hrApi.downloadIpbTemplate() }}>
          Vorlage CSV
        </button>
        <label className="text-xs border rounded-md px-2.5 py-1.5 bg-white cursor-pointer">
          CSV / Excel wählen
          <input type="file" accept=".csv,.xlsx,text/csv" className="hidden"
            onChange={e => { void onFile(e.target.files?.[0]); e.target.value = '' }} />
        </label>
        {busy && <span className="text-xs text-gray-500">Bitte warten…</span>}
      </div>
      {preview && (
        <>
          <div className="text-xs text-gray-500">
            {preview.changed} Änderung(en) · {preview.unchanged} unverändert · {preview.unmatched.length} ohne Treffer
          </div>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-3 py-2"></th>
                  <th className="text-left px-3 py-2">Lehrer</th>
                  <th className="text-left px-3 py-2">Intern</th>
                  <th className="text-left px-3 py-2">Import</th>
                  <th className="text-left px-3 py-2">Differenz</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {preview.matched.map(r => (
                  <tr key={r.teacher_id} className={r.delta === 0 ? 'text-gray-400' : ''}>
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={selected.has(r.teacher_id)}
                        disabled={r.delta === 0}
                        onChange={() => setSelected(s => {
                          const n = new Set(s)
                          if (n.has(r.teacher_id)) n.delete(r.teacher_id)
                          else n.add(r.teacher_id)
                          return n
                        })} />
                    </td>
                    <td className="px-3 py-2 font-medium">
                      {r.teacher_name}
                      {r.teacher_code ? <span className="text-gray-400 text-xs ml-1.5">({r.teacher_code})</span> : null}
                    </td>
                    <td className="px-3 py-2">{fmt(r.current_saldo)}</td>
                    <td className="px-3 py-2">{fmt(r.import_saldo)}</td>
                    <td className={`px-3 py-2 font-medium ${r.delta > 0 ? 'text-green-600' : r.delta < 0 ? 'text-red-600' : ''}`}>
                      {r.delta > 0 ? '+' : ''}{fmt(r.delta)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.unmatched.length > 0 && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg p-3">
              <div className="font-semibold mb-1">Ohne Treffer</div>
              {preview.unmatched.map(u => (
                <div key={`${u.row}-${u.teacher_code}`}>Zeile {u.row}: {u.teacher_code || u.email || '—'} — {u.reason}</div>
              ))}
            </div>
          )}
          <button type="button" disabled={busy || selected.size === 0} onClick={() => void apply()}
            className="px-4 py-2 text-sm bg-green-600 text-white rounded-md disabled:opacity-50">
            Auswahl setzen ({selected.size})
          </button>
        </>
      )}
    </div>
  )
}

// ===========================================
// Kontoauszug
// ===========================================

const TX_LABELS = TX_TYPE_LABELS

function StatementTab({ onError }: { onError: (s: string) => void }) {
  const [params] = useSearchParams()
  const preselect = params.get('teacher') || ''
  const [rows, setRows] = useState<HrSaldoRow[]>([])
  const [teacherId, setTeacherId] = useState(preselect)
  const [stmt, setStmt] = useState<HrStatementResponse | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    hrApi.getSalden({ include_zero: true }).then(r => setRows(r.salden)).catch(() => {})
  }, [])

  const load = useCallback(async (tid: string) => {
    if (!tid) return
    setLoading(true)
    try {
      setStmt(await hrApi.getStatement(tid))
    } catch (e: unknown) {
      onError(extractErrorMessage(e, { loadError: 'Auszug konnte nicht geladen werden' }))
    } finally { setLoading(false) }
  }, [onError])

  useEffect(() => {
    if (preselect) {
      setTeacherId(preselect)
      void load(preselect)
    }
  }, [preselect, load])

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <select value={teacherId} onChange={e => { setTeacherId(e.target.value); load(e.target.value) }}
          className="text-sm border border-gray-300 rounded-md px-3 py-2 bg-white min-w-64">
          <option value="">Lehrer wählen…</option>
          {preselect && !rows.some(r => r.teacher_id === preselect) && (
            <option value={preselect}>{stmt?.teacher_id === preselect ? stmt.teacher_name : 'Gewählter Lehrer'}</option>
          )}
          {rows.map(r => <option key={r.teacher_id} value={r.teacher_id}>{r.teacher_name}</option>)}
        </select>
        {stmt && <span className="text-sm">Saldo: <strong>{fmt(stmt.saldo)}</strong> Lektionen</span>}
      </div>
      {loading && <div className="text-gray-500 text-sm">Laden...</div>}
      {stmt && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Datum</th>
                <th className="text-left px-4 py-2 font-medium">Typ</th>
                <th className="text-left px-4 py-2 font-medium">Fach</th>
                <th className="text-left px-4 py-2 font-medium">Kostenträger</th>
                <th className="text-left px-4 py-2 font-medium">Grund</th>
                <th className="text-right px-4 py-2 font-medium">Betrag</th>
                <th className="text-right px-4 py-2 font-medium">Saldo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {stmt.transactions.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-4 text-center text-gray-500">Keine Buchungen</td></tr>
              )}
              {stmt.transactions.map(t => (
                <tr key={t.id}>
                  <td className="px-4 py-2.5 text-xs text-gray-500">
                    {/* hr2-v2 (E9): de-CH-Datumsformat statt Roh-Slice */}
                    {t.created_at ? new Date(t.created_at).toLocaleString('de-CH', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                  </td>
                  <td className="px-4 py-2.5">{t.source === 'import' ? 'Import' : (TX_LABELS[t.tx_type] ?? t.tx_type)}</td>
                  <td className="px-4 py-2.5">{t.subject_name ?? '—'}</td>
                  <td className="px-4 py-2.5 text-xs">{t.cost_bearer_name ?? '—'}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500 max-w-64 truncate">{t.reason ?? ''}</td>
                  <td className={`px-4 py-2.5 text-right font-medium ${t.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {t.amount > 0 ? '+' : ''}{fmt(t.amount)}
                  </td>
                  <td className="px-4 py-2.5 text-right">{fmt(t.running_saldo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ===========================================
// Manuelle Buchung
// ===========================================

function ManualTab({ onError, onNotice }: { onError: (s: string) => void; onNotice: (s: string) => void }) {
  const [rows, setRows] = useState<HrSaldoRow[]>([])
  const [form, setForm] = useState({ teacher_id: '', direction: 'credit', amount: '', reason: '' })
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    hrApi.getSalden({ include_zero: true }).then(r => setRows(r.salden)).catch(() => {})
  }, [])

  const submit = async () => {
    setBusy(true); onError('')
    try {
      await hrApi.createManualTx({
        teacher_id: form.teacher_id,
        direction: form.direction as 'credit' | 'debit',
        amount: parseFloat(form.amount),
        reason: form.reason,
      })
      onNotice('Buchung erfasst (unveränderlich — Korrekturen als neue Buchung).')
      setForm({ teacher_id: form.teacher_id, direction: 'credit', amount: '', reason: '' })
    } catch (e: unknown) {
      onError(extractErrorMessage(e, { loadError: 'Buchung fehlgeschlagen' }))
    } finally { setBusy(false) }
  }

  return (
    <div className="bg-white rounded-lg shadow p-4 max-w-xl">
      <div className="space-y-3">
        <label className="block text-sm text-gray-600">
          Lehrer
          <select value={form.teacher_id} onChange={e => setForm(f => ({ ...f, teacher_id: e.target.value }))}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 bg-white">
            <option value="">Wählen…</option>
            {rows.map(r => <option key={r.teacher_id} value={r.teacher_id}>{r.teacher_name}</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm text-gray-600">
            Richtung
            <select value={form.direction} onChange={e => setForm(f => ({ ...f, direction: e.target.value }))}
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 bg-white">
              <option value="credit">Gutschrift (+)</option>
              <option value="debit">Abbuchung (−)</option>
            </select>
          </label>
          <label className="block text-sm text-gray-600">
            Lektionen (Dezimal)
            <input type="number" step="0.01" min="0.01" value={form.amount}
              onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" />
          </label>
        </div>
        <label className="block text-sm text-gray-600">
          Grund
          <input type="text" value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" />
        </label>
        <button type="button" disabled={busy || !form.teacher_id || !form.amount || !form.reason.trim()} onClick={submit}
          className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50">
          Buchen
        </button>
      </div>
    </div>
  )
}

// ===========================================
// Perioden
// ===========================================

function PeriodsTab({ onError, onNotice }: { onError: (s: string) => void; onNotice: (s: string) => void }) {
  const [periods, setPeriods] = useState<HrPeriodInfo[]>([])
  const [accountingMode, setAccountingMode] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    hrApi.getPeriods()
      .then(r => { setPeriods(r.periods); setAccountingMode(r.accounting_mode) })
      .catch(e => onError(extractErrorMessage(e, { loadError: 'Perioden konnten nicht geladen werden' })))
  }, [onError])

  const close = async (p: HrPeriodInfo) => {
    if (!window.confirm(`Periode «${p.name}» abschliessen? Bei jedem Lehrer mit Saldo ≠ 0 wird eine Abschluss-Buchung (Saldo → 0) erstellt.`)) return
    setBusy(true); onError('')
    try {
      const r: HrPeriodCloseResult = await hrApi.closePeriod(p.key)
      onNotice(`Periode ${p.key} abgeschlossen: ${r.closed_teachers.length} Lehrer zurückgesetzt.` +
        (r.open_cases_remaining > 0 ? ` Hinweis: ${r.open_cases_remaining} offene Fälle in dieser Periode.` : ''))
    } catch (e: unknown) {
      onError(extractErrorMessage(e, { loadError: 'Abschluss fehlgeschlagen' }))
    } finally { setBusy(false) }
  }

  return (
    <div>
      {accountingMode !== 'periodic' && (
        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded">
          Kontoführung ist «{accountingMode === 'cumulative' ? 'kumulativ' : accountingMode}» —
          der Periodenabschluss (Saldo-Reset) ist nur im Modus «periodisch» möglich.
        </div>
      )}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Periode</th>
              <th className="text-left px-4 py-2 font-medium">Typ</th>
              <th className="text-left px-4 py-2 font-medium">Von</th>
              <th className="text-left px-4 py-2 font-medium">Bis</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {periods.map(p => (
              <tr key={p.key}>
                <td className="px-4 py-3 font-medium">{p.name}</td>
                <td className="px-4 py-3 text-gray-500">{periodTypeLabel(p.type)}</td>
                <td className="px-4 py-3">{formatHrDate(p.start)}</td>
                <td className="px-4 py-3">{formatHrDate(p.end)}</td>
                <td className="px-4 py-3 text-right">
                  <button type="button" disabled={busy || accountingMode !== 'periodic'} onClick={() => close(p)}
                    className="text-xs px-2.5 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">
                    Abschliessen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ===========================================
// Lohn-Export (Feldauswahl, D6: Lektionen)
// ===========================================

function ExportTab({ onError, onNotice }: { onError: (s: string) => void; onNotice: (s: string) => void }) {
  const [selected, setSelected] = useState<string[]>(
    ['teacher_name', 'teacher_code', 'payout_lessons', 'ipb_credits', 'saldo'])
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [format, setFormat] = useState<'csv' | 'pdf'>('csv')
  const [busy, setBusy] = useState(false)

  const toggle = (key: string) =>
    setSelected(s => s.includes(key) ? s.filter(k => k !== key) : [...s, key])

  const download = async () => {
    setBusy(true); onError('')
    try {
      // R10 (hr-v3): Download über apiClient (Blob) — Token-Refresh/401-Logik
      // bleibt zentral statt im rohen fetch dupliziert.
      const resp = await apiClient.post('/api/v1/hr/ipb/salary-export', {
        fields: selected,
        date_from: from || null,
        date_to: to || null,
        format,
      }, { responseType: 'blob' })
      const blob = resp.data as Blob
      const disposition: string = resp.headers?.['content-disposition'] ?? ''
      const match = disposition.match(/filename\*=UTF-8''([^;]+)/)
      const filename = match?.[1] ? decodeURIComponent(match[1]) : `lohnliste.${format}`
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = filename; a.click()
      URL.revokeObjectURL(url)
      onNotice(`Export erstellt (${filename}).`)
    } catch (e: unknown) {
      onError(extractErrorMessage(e, { loadError: 'Export fehlgeschlagen' }))
    } finally { setBusy(false) }
  }

  return (
    <div className="bg-white rounded-lg shadow p-4 max-w-xl">
      <h3 className="font-semibold mb-1">Lohn-Export</h3>
      <p className="text-xs text-gray-500 mb-3">
        Reine Datei für das externe Lohnsystem. Alle Werte in Lektionen (Dezimal) —
        keine Geldbeträge (Umrechnung out of scope).
      </p>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm text-gray-600">
            Von (optional)
            <input type="date" value={from} onChange={e => setFrom(e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" />
          </label>
          <label className="text-sm text-gray-600">
            Bis (optional)
            <input type="date" value={to} onChange={e => setTo(e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" />
          </label>
        </div>
        <div>
          <div className="text-sm text-gray-600 mb-1.5">Felder (togglebar)</div>
          <div className="grid grid-cols-2 gap-1.5">
            {SALARY_EXPORT_FIELDS.map(f => (
              <label key={f.key} className="text-xs flex items-center gap-2 border border-gray-200 rounded px-2 py-1.5 cursor-pointer hover:bg-gray-50">
                <input type="checkbox" checked={selected.includes(f.key)} onChange={() => toggle(f.key)} />
                {f.label}
              </label>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select value={format} onChange={e => setFormat(e.target.value as 'csv' | 'pdf')}
            className="text-sm border border-gray-300 rounded-md px-3 py-2 bg-white">
            <option value="csv">CSV</option>
            <option value="pdf">PDF</option>
          </select>
          <button type="button" disabled={busy || selected.length === 0} onClick={download}
            className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 inline-flex items-center gap-2">
            <Download className="w-4 h-4" /> Export starten
          </button>
        </div>
      </div>
    </div>
  )
}

export default HRIpbPage
